import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

/**
 * A régua única do faturamento, para as filas do Kanban e as triagens.
 *
 * Douglas 17/08/2026, olhando a Triagem Biometria: "está marcando o faturamento
 * dos aparelhos somando as vendas na bio... não pode, ele não é junto é
 * separado". As filas somavam `subtotal` de TODA linha, então o APARELHO — que
 * tem KPI próprio (`FAT. APARELHOS`) e NÃO é faturamento de venda — entrava no
 * contador de R$. Na Bio eram R$ 16.398,00 de aparelho dentro de R$ 29.470,14:
 * 56% do número. A tela dizia que a fila segurava um faturamento que ela não
 * segura, e o BKO priorizava por um valor inflado.
 *
 * Faturamento = o KPI `FAT. TOTAL` do cadastro do produto, e SÓ ele — a mesma
 * régua da Visão Prime, da Planilha e da Venda Diária. Isso também tira dos
 * contadores os produtos NÃO PAGO (TT FIXA, AGENDA AV, TOTALIZAÇÃO…), que nunca
 * foram faturamento. Ligar/desligar um produto é toggle em Configurações ▸
 * Produtos, sem deploy.
 */
export interface FlagsFaturamento {
  /** Nomes (UPPER+TRIM) dos produtos com `FAT. TOTAL` ligado. */
  faturamento: Set<string>;
  /** Nomes (UPPER+TRIM) dos produtos com `FAT. APARELHOS` ligado. */
  aparelhos: Set<string>;
}

interface ProdutoFlagsRow {
  nome: string;
  fat_total: boolean | null;
  fat_aparelhos: boolean | null;
}

const VAZIO: FlagsFaturamento = { faturamento: new Set(), aparelhos: new Set() };

export const normalizarProduto = (nome: unknown) => String(nome || '').toUpperCase().trim();

/**
 * Lê as flags do cadastro. Cache longo de propósito: a matriz de KPIs muda
 * raramente, e toda triagem aberta na mesma sessão reaproveita a mesma resposta.
 */
export function useFlagsFaturamento() {
  const { data } = useQuery<FlagsFaturamento>({
    queryKey: ['flags-faturamento'],
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    queryFn: async () => {
      const { data: rows, error } = await supabase
        .from('produtos')
        .select('nome, fat_total, fat_aparelhos');
      if (error) throw error;
      const flags: FlagsFaturamento = { faturamento: new Set(), aparelhos: new Set() };
      for (const p of (rows || []) as ProdutoFlagsRow[]) {
        const nome = normalizarProduto(p.nome);
        if (!nome) continue;
        if (p.fat_total) flags.faturamento.add(nome);
        if (p.fat_aparelhos) flags.aparelhos.add(nome);
      }
      return flags;
    },
  });
  return data ?? VAZIO;
}

/**
 * Quebra uma lista de itens nas DUAS moedas da fila — nunca somadas.
 *
 * ⚠️ Enquanto as flags não chegaram (primeira renderização), devolve tudo como
 * faturamento em vez de zerar: contador piscando em R$ 0,00 assusta mais do que
 * o número antigo, e a correção aparece no instante seguinte.
 */
/** Quantos aparelhos a linha leva: a `quantidade` dela, ou 1. */
export const quantidadeDaLinha = (item: unknown) => Number((item as { quantidade?: unknown } | null)?.quantidade) || 1;

/* A pill do aparelho fala em QUANTIDADE, não em R$ (Douglas 30/10/2026, "sim
   todas"): o R$ do aparelho não é faturamento e confundia a leitura da fila.
   `qtdAparelhos` é o que as triagens mostram; `aparelhos` (R$) fica para o
   Kanban e o detalhe da cotação. */
export function separarAparelho<T>(
  itens: T[],
  flags: FlagsFaturamento,
  produtoDe: (item: T) => unknown,
  valorDe: (item: T) => number,
  qtdDe: (item: T) => number = quantidadeDaLinha,
): { faturamento: number; aparelhos: number; qtdAparelhos: number } {
  const carregou = flags.faturamento.size > 0 || flags.aparelhos.size > 0;
  let faturamento = 0;
  let aparelhos = 0;
  let qtdAparelhos = 0;
  for (const item of itens) {
    const valor = valorDe(item) || 0;
    if (!carregou) {
      faturamento += valor;
      continue;
    }
    const nome = normalizarProduto(produtoDe(item));
    if (flags.aparelhos.has(nome)) {
      aparelhos += valor;
      qtdAparelhos += qtdDe(item);
    } else if (flags.faturamento.has(nome)) faturamento += valor;
  }
  return { faturamento, aparelhos, qtdAparelhos };
}
